import type {
  SelectFilterOption,
  SelectOption,
  SelectValue,
} from 'sutra/components/Select/select-types'

type AnyOption = SelectOption<SelectValue>

// The plain text of an option: what the default filter matches and what a
// chip's remove button is named after. `optionFilterProp` names another field
// on the option; otherwise a string label, then `searchText`, then the value.
export const optionText = (
  option: AnyOption,
  optionFilterProp?: string,
): string => {
  if (optionFilterProp !== undefined) {
    const field = (option as unknown as Record<string, unknown>)[
      optionFilterProp
    ]
    if (typeof field === 'string' || typeof field === 'number') {
      return String(field)
    }
  }
  if (option.searchText !== undefined) return option.searchText
  if (typeof option.label === 'string' || typeof option.label === 'number') {
    return String(option.label)
  }
  return String(option.value)
}

interface FilterArgs<O extends AnyOption> {
  query: string
  filterOption?: SelectFilterOption<O>
  filterSort?: (a: O, b: O) => number
  optionFilterProp?: string
}

// An empty query shows everything, unsorted. `filterOption={false}` shows
// everything for any query (the caller searches elsewhere).
export const filterOptions = <O extends AnyOption>(
  options: readonly O[],
  { query, filterOption, filterSort, optionFilterProp }: FilterArgs<O>,
): O[] => {
  const text = query.trim()
  if (text === '' || filterOption === false) return [...options]
  const lower = text.toLowerCase()
  const matches =
    typeof filterOption === 'function'
      ? (option: O): boolean => filterOption(query, option)
      : (option: O): boolean =>
          optionText(option, optionFilterProp).toLowerCase().includes(lower)
  const kept = options.filter(matches)
  return filterSort ? kept.sort(filterSort) : kept
}

export const findOption = <O extends AnyOption>(
  options: readonly O[],
  value: SelectValue | null | undefined,
): O | undefined => options.find((option) => option.value === value)

// The text a `tags` select would add as its own value, or null when there is
// nothing to add: an empty query, or one that already names an option or a
// chip.
export const creatableText = (
  query: string,
  options: readonly AnyOption[],
  values: readonly SelectValue[],
): string | null => {
  const text = query.trim()
  if (text === '') return null
  const lower = text.toLowerCase()
  const taken =
    options.some(
      (option) =>
        String(option.value).toLowerCase() === lower ||
        optionText(option).toLowerCase() === lower,
    ) || values.some((value) => String(value).toLowerCase() === lower)
  return taken ? null : text
}

// A token typed in full names an option by its value or text; anything else
// is free text and becomes its own value.
export const resolveToken = (
  token: string,
  options: readonly AnyOption[],
): SelectValue => {
  const lower = token.toLowerCase()
  const match = options.find(
    (option) =>
      String(option.value).toLowerCase() === lower ||
      optionText(option).toLowerCase() === lower,
  )
  return match ? match.value : token
}
